import React, { useState, Children } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User as UserIcon, Eye, EyeOff, ArrowRight, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
function mapAuthError(err: any): string {
  const code = err?.code ?? '';
  if (code === 'auth/email-already-in-use') return 'An account with this email already exists. Try signing in instead.';
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') return 'Invalid email or password. Please try again.';
  if (code === 'auth/weak-password') return 'Password must be at least 6 characters.';
  if (code === 'auth/invalid-email') return 'Please enter a valid email address.';
  if (code === 'auth/too-many-requests') return 'Too many attempts. Please wait a moment and try again.';
  return err?.message ?? 'Something went wrong. Please try again.';
}
// ─── Shared Layout ──────────────────────────────────────────────
function AuthShell({
  eyebrow,
  title,
  subtitle,
  children
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return <div className="w-full min-h-screen bg-bodybg" data-id="element-1432">
      {/* HERO */}
      <section className="bg-bodybg pt-40 pb-12 px-6 border-b border-darkgray" data-id="element-1433">
        <div className="max-w-2xl mx-auto text-center" data-id="element-1434">
          <motion.div initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.12
            }
          }
        }} className="flex flex-col items-center" data-id="element-1435">
            <motion.p variants={fadeUp} className="text-[10px] md:text-xs font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-6" data-id="element-1436">
              {eyebrow}
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl font-serif text-white leading-tight mb-6" data-id="element-1437">
              {title}
            </motion.h1>
            <motion.div variants={fadeUp} className="w-16 h-0.5 bg-tealslate mb-6" data-id="element-1438" />
            <motion.p variants={fadeUp} className="text-base text-[#D8D8D0] max-w-lg leading-relaxed" data-id="element-1439">
              {subtitle}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FORM */}
      <section className="bg-[#F5F5F0] py-16 md:py-24 px-6" data-id="element-1440">
        <div className="max-w-md mx-auto" data-id="element-1441">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} data-id="element-1442">
            {children}
          </motion.div>
        </div>
      </section>
    </div>;
}
function FieldLabel({
  htmlFor,
  children
}: {
  htmlFor: string;
  children: React.ReactNode;
}) {
  return <label htmlFor={htmlFor} className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-1443">
      {children}
    </label>;
}
function ErrorBanner({
  message
}: {
  message: string;
}) {
  return <div className="bg-burgundy/10 border-l-4 border-burgundy p-4 flex gap-3 items-start" data-id="element-1444">
      <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-1445" />
      <p className="text-sm text-burgundy" data-id="element-1446">{message}</p>
    </div>;
}
function SubmitButton({
  loading,
  loadingLabel,
  children
}: {
  loading: boolean;
  loadingLabel: string;
  children: React.ReactNode;
}) {
  return <button type="submit" disabled={loading} className="w-full px-10 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-3" data-id="element-1447">
      {loading ? <>
          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1448" />
          {loadingLabel}
        </> : <>
          {children} <ArrowRight className="w-4 h-4" data-id="element-1449" />
        </>}
    </button>;
}
// ─── Login ──────────────────────────────────────────────────────
export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    signIn
  } = useAuth();
  const redirectTo = (location.state as {
    from?: string;
  } | null)?.from ?? '/account';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signIn(email, password);
      navigate(redirectTo, {
        replace: true
      });
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };
  return <AuthShell eyebrow="Sign In" title="Welcome back." subtitle="Sign in to view your orders, downloads, and account details." data-id="element-1450">
      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1451">
        <div data-id="element-1452">
          <FieldLabel htmlFor="email" data-id="element-1453">Email</FieldLabel>
          <div className="relative" data-id="element-1454">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-1455" />
            <input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-[#D8D8D0] pl-11 pr-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="you@example.com" data-id="element-1456" />
          </div>
        </div>

        <div data-id="element-1457">
          <FieldLabel htmlFor="password" data-id="element-1458">Password</FieldLabel>
          <div className="relative" data-id="element-1459">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-1460" />
            <input id="password" type={showPassword ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white border border-[#D8D8D0] pl-11 pr-12 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="••••••••" data-id="element-1461" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9A9A90] hover:text-tealslate transition-colors" tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-1462">
              {showPassword ? <EyeOff className="w-4 h-4" data-id="element-1463" /> : <Eye className="w-4 h-4" data-id="element-1464" />}
            </button>
          </div>
          <div className="mt-3 text-right" data-id="element-1465">
            <Link to="/forgot-password" className="text-xs font-bold text-tealslate uppercase tracking-[0.2em] hover:text-navyslate transition-colors" data-id="element-1466">
              Forgot password?
            </Link>
          </div>
        </div>

        {error && <ErrorBanner message={error} data-id="element-1467" />}

        <SubmitButton loading={loading} loadingLabel="Signing in…" data-id="element-1468">
          Sign In
        </SubmitButton>

        <p className="text-sm text-[#3B3B3B] text-center pt-4" data-id="element-1469">
          Don't have an account?{' '}
          <Link to="/signup" className="font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-1470">
            Create one
          </Link>
        </p>
      </form>
    </AuthShell>;
}
// ─── Sign Up ────────────────────────────────────────────────────
export function Signup() {
  const navigate = useNavigate();
  const {
    signUp
  } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    setLoading(true);
    try {
      await signUp(email, password, name.trim(), marketingOptIn);
      navigate('/account', {
        replace: true
      });
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };
  return <AuthShell eyebrow="Create Account" title="Start your journey." subtitle="Create an account to track your orders, access digital downloads, and stay connected." data-id="element-1471">
      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1472">
        <div data-id="element-1473">
          <FieldLabel htmlFor="name" data-id="element-1474">Full Name</FieldLabel>
          <div className="relative" data-id="element-1475">
            <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-1476" />
            <input id="name" type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-white border border-[#D8D8D0] pl-11 pr-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="Jane Smith" data-id="element-1477" />
          </div>
        </div>

        <div data-id="element-1478">
          <FieldLabel htmlFor="email" data-id="element-1479">Email</FieldLabel>
          <div className="relative" data-id="element-1480">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-1481" />
            <input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-[#D8D8D0] pl-11 pr-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="you@example.com" data-id="element-1482" />
          </div>
        </div>

        <div data-id="element-1483">
          <FieldLabel htmlFor="password" data-id="element-1484">Password</FieldLabel>
          <div className="relative" data-id="element-1485">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-1486" />
            <input id="password" type={showPassword ? 'text' : 'password'} required minLength={6} value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white border border-[#D8D8D0] pl-11 pr-12 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="At least 6 characters" data-id="element-1487" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9A9A90] hover:text-tealslate transition-colors" tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-1488">
              {showPassword ? <EyeOff className="w-4 h-4" data-id="element-1489" /> : <Eye className="w-4 h-4" data-id="element-1490" />}
            </button>
          </div>
        </div>

        <label className="flex items-start gap-3 cursor-pointer group" data-id="element-1491">
          <input type="checkbox" checked={marketingOptIn} onChange={e => setMarketingOptIn(e.target.checked)} className="mt-1 w-4 h-4 accent-tealslate cursor-pointer" data-id="element-1492" />
          <span className="text-sm text-[#3B3B3B] leading-relaxed" data-id="element-1493">
            Send me occasional updates on new books, supplements, and articles.
            No spam — unsubscribe anytime.
          </span>
        </label>

        {error && <ErrorBanner message={error} data-id="element-1494" />}

        <SubmitButton loading={loading} loadingLabel="Creating account…" data-id="element-1495">
          Create Account
        </SubmitButton>

        <p className="text-sm text-[#3B3B3B] text-center pt-4" data-id="element-1496">
          Already have an account?{' '}
          <Link to="/login" className="font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-1497">
            Sign in
          </Link>
        </p>

        <p className="text-xs text-[#7A7A70] italic text-center pt-2" data-id="element-1498">
          By creating an account, you agree to receive a verification email.
        </p>
      </form>
    </AuthShell>;
}
// ─── Forgot Password ────────────────────────────────────────────
export function ForgotPassword() {
  const {
    resetPassword
  } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await resetPassword(email);
      setSent(true);
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };
  return <AuthShell eyebrow="Reset Password" title="Forgot your password?" subtitle="Enter your email and we'll send you a link to reset it." data-id="element-1499">
      {sent ? <div className="bg-white border-l-4 border-tealslate p-8 flex gap-5" data-id="element-1500">
          <CheckCircle2 className="w-8 h-8 text-tealslate shrink-0" data-id="element-1501" />
          <div data-id="element-1502">
            <h3 className="text-2xl font-serif text-[#1F1F1F] mb-2" data-id="element-1503">
              Check your email.
            </h3>
            <p className="text-[#3B3B3B] leading-relaxed mb-4" data-id="element-1504">
              If an account exists for <strong data-id="element-1505">{email}</strong>, we've sent a
              password reset link. Check your spam folder if you don't see it.
            </p>
            <Link to="/login" className="text-xs font-bold text-tealslate uppercase tracking-wider hover:text-navyslate transition-colors inline-flex items-center gap-2" data-id="element-1506">
              Back to sign in <ArrowRight className="w-3 h-3" data-id="element-1507" />
            </Link>
          </div>
        </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1508">
          <div data-id="element-1509">
            <FieldLabel htmlFor="email" data-id="element-1510">Email</FieldLabel>
            <div className="relative" data-id="element-1511">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-1512" />
              <input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-[#D8D8D0] pl-11 pr-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="you@example.com" data-id="element-1513" />
            </div>
          </div>

          {error && <ErrorBanner message={error} data-id="element-1514" />}

          <SubmitButton loading={loading} loadingLabel="Sending…" data-id="element-1515">
            Send Reset Link
          </SubmitButton>

          <p className="text-sm text-[#3B3B3B] text-center pt-4" data-id="element-1516">
            Remembered it?{' '}
            <Link to="/login" className="font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-1517">
              Back to sign in
            </Link>
          </p>
        </form>}
    </AuthShell>;
}